"use client";

/**
 * VoterDropdown — anchored popover showing voter list
 *
 * Figma 5462:53124 panel / 5462:53234 populated.
 * Max-h 256px scrollable. Each row: WalletAddress + nickname + tick icon.
 * Triggered on first open via useProposalVoters(txIndex, cycleCount, enabled).
 */

import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { WalletAddress } from "@/components/core/WalletAddress";
import { useProposalVoters } from "@/hooks/dao/useProposalVoters";

interface VoterDropdownProps {
  txIndex: number;
  cycleCount: number;
}

export function VoterDropdown({ txIndex, cycleCount }: VoterDropdownProps) {
  const t = useTranslations("dao");
  // Parent (MetaRow) only mounts this component when the dropdown is open,
  // so being mounted ⇒ should fetch. No internal `opened` flag needed.
  const { voters, isLoading } = useProposalVoters(txIndex, cycleCount, true);

  return (
    <div className="flex flex-col">
      {isLoading && (
        <div className="px-3 py-2 text-sm text-text-secondary">
          {t("proposals.detail.voters.loading")}
        </div>
      )}
      {!isLoading && voters.length === 0 && (
        <div className="px-3 py-2 text-sm text-text-secondary">
          {t("proposals.detail.voters.empty")}
        </div>
      )}
      {voters.map((voter) => (
        <div
          key={voter.address}
          className="flex items-center justify-between px-3 py-2 gap-3"
        >
          <div className="flex flex-col min-w-0">
            <span className="text-sm text-text-primary truncate">
              {voter.nickname ?? t("proposals.detail.voters.noName")}
            </span>
            <WalletAddress address={voter.address} size={14} canCopy />
          </div>
          <Check size={18} className="text-text-primary shrink-0" />
        </div>
      ))}
    </div>
  );
}
